import { Icon, type IconType } from "@pepperstone/pepper-ui-kit";
import {
  type ChangeEventHandler,
  type DragEventHandler,
  type PropsWithChildren,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";

type UploadProps = {
  accept: string;
  isUploadDisabled?: boolean;
  multiple?: boolean;
};

type FileDropzoneProps = PropsWithChildren & {
  uploadProps: UploadProps;
  testId?: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
};

export function FileDropzone({
  testId = "file-upload",
  uploadProps,
  children,
  onChange,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      onChange(e);
      // Reset the input value so selecting the same file again still fires
      // onChange (e.g. after the consumer removes a file and re-adds it).
      e.target.value = "";
    },
    [onChange]
  );

  const handleDragOver = useCallback<DragEventHandler<HTMLDivElement>>((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback<DragEventHandler<HTMLDivElement>>((e) => {
    e.preventDefault();
    e.stopPropagation();
    const input = inputRef.current;
    const { files } = e.dataTransfer;
    if (!input || files.length === 0) return;
    input.files = files;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }, []);

  return (
    <div className="ps-flex ps-flex-col ps-gap-1 ps-mt-2">
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          display: "grid",
          placeItems: "center",
          backgroundColor:
            "rgb(var(--ps-color-neutral-100) / var(--tw-bg-opacity))!important",
          borderRadius: "8px",
          borderWidth: "1px",
          borderStyle: "dashed",
          borderColor: "rgb(var(--ps-color-neutral-400) / 1)!important",
          opacity: uploadProps.isUploadDisabled ? 0.5 : 1,
          cursor: uploadProps.isUploadDisabled ? "not-allowed" : "pointer",
        }}
      >
        {uploadProps.isUploadDisabled ? null : (
          <input
            ref={inputRef}
            style={{
              gridArea: "1/1",
              width: "100%",
              height: "100%",
              opacity: 0,
            }}
            type="file"
            name="file"
            id="file"
            data-testid={`${testId}-file-input`}
            disabled={uploadProps.isUploadDisabled}
            accept={uploadProps.accept}
            multiple={uploadProps.multiple}
            onChange={handleChange}
          />
        )}
        <div
          style={{
            margin: "24px",
            gap: "16px",
            display: "flex",
            flexDirection: "column",
            gridArea: "1/1",
            placeItems: "center",
            pointerEvents: "none",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export const FileDropzoneIcon = ({
  icon,
  size,
  testId = "file-upload",
}: {
  icon: IconType;
  size?: number;
  testId?: string;
}) => {
  return (
    <div
      data-testid={`${testId}-dropzone-icon`}
      style={{
        backgroundColor: "rgba(3, 20, 46, 0.70)",
        color: "white",
        padding: "8px",
        borderRadius: "50%",
      }}
    >
      <Icon icon={icon} size={size || 24} />
    </div>
  );
};

export const FileDropzoneText = ({
  children,
  dropFilesText,
  browseFilesText,
  fileLimitationsText,
  proofPromptText,
  testId = "file-upload",
}: PropsWithChildren & {
  dropFilesText?: string;
  browseFilesText?: string;
  fileLimitationsText?: string;
  proofPromptText?: string;
  testId?: string;
}) => {
  const { t } = useTranslation();

  return (
    children ?? (
      <div data-testid={`${testId}-dropzone-text`}>
        <p className="ps-text-center">
          {dropFilesText ??
            t("deposit.methods.saved_methods.upload_proof_drop_files_or")}{" "}
          <label
            className="ps-underline ps-text-text-link-base"
            htmlFor="file"
            style={{ pointerEvents: "auto" }}
          >
            {browseFilesText ??
              t("deposit.methods.saved_methods.upload_proof_browse_files")}
          </label>
        </p>
        <p className="ps-text-v2-text-base-subdued ps-text-body-v2-sm ps-text-center">
          {fileLimitationsText ??
            t("deposit.methods.saved_methods.upload_proof_file_limitations")}
        </p>
        <p className="ps-text-v2-text-base-subdued ps-text-body-v2-sm ps-text-center">
          {proofPromptText ??
            t("deposit.methods.saved_methods.upload_proof_prompt")}
        </p>
      </div>
    )
  );
};

export const FileDropzoneDisabledMessage = ({
  message,
  testId = "file-upload",
}: {
  message: string;
  testId?: string;
}) => {
  return (
    <p
      data-testid={`${testId}-disabled-message`}
      className="ps-text-v2-text-base-subdued ps-text-body-v2-sm ps-text-center"
    >
      {message}
    </p>
  );
};

FileDropzone.FileDropzoneIcon = FileDropzoneIcon;
FileDropzone.FileDropzoneText = FileDropzoneText;
FileDropzone.FileDropzoneDisabledMessage = FileDropzoneDisabledMessage;
